
import { useEffect, useRef, useState } from 'react';
import {
  DotsSixVerticalIcon,
  EyeIcon,
  EyeSlashIcon,
  PlusCircleIcon,
  MagnifyingGlassIcon,
  TagIcon,
  LockIcon,
  CircleNotchIcon,
  SlidersHorizontalIcon,
} from '@phosphor-icons/react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import {
  DndContext,
  closestCenter,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useDragDropSensors } from '@/hooks/useDragDropSensors';

interface ColumnItem {
  id: string;
  label: string;
  description?: string;
  isVisible: boolean;
  isFixed?: boolean;
  isSystem?: boolean;
  isCustomField?: boolean;
  order?: number;
}

interface FieldTypeOption {
  value: string;
  label: string;
  description: string;
}

const FIELD_TYPE_CATEGORIES = [
  {
    id: 'text',
    label: 'Text',
    types: ['STRING', 'TEXT', 'RICH_TEXT'],
  },
  {
    id: 'number',
    label: 'Number',
    types: ['NUMBER', 'PERCENTAGE', 'CURRENCY'],
  },
  {
    id: 'dateTime',
    label: 'Date & Time',
    types: ['DATE', 'DATETIME', 'TIME'],
  },
  {
    id: 'selection',
    label: 'Selection',
    types: ['DROPDOWN', 'MULTISELECT', 'BOOLEAN'],
  },
  {
    id: 'reference',
    label: 'Reference',
    types: ['REFERENCE', 'MULTI_REFERENCE', 'USER', 'MULTI_USER'],
  },
  {
    id: 'files',
    label: 'Files',
    types: ['FILE', 'ATTACHMENTS'],
  },
  {
    id: 'other',
    label: 'Other',
    types: ['LINK'],
  },
];

interface TableCustomizerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  columns: ColumnItem[];
  onColumnVisibilityChange: (columnId: string, isVisible: boolean) => Promise<void>;
  onColumnsReorder: (reorderedColumns: ColumnItem[]) => Promise<void>;
  fieldTypeOptions?: FieldTypeOption[];
  onFieldTypeSelect?: (fieldType: string) => void;
  isLoading?: boolean;
  canModify?: boolean;
  title?: string;
  description?: string;
}

interface SortableColumnRowProps {
  column: ColumnItem;
  onToggleVisibility: (isVisible: boolean) => void;
  isDisabled: boolean;
  isUpdating: boolean;
}

function SortableColumnRow({
  column,
  onToggleVisibility,
  isDisabled,
  isUpdating,
}: SortableColumnRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: column.id,
    disabled: isDisabled || column.isFixed,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : undefined,
  };

  const isDraggable = !isDisabled && !column.isFixed;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "group flex items-center gap-2 px-3 py-2 rounded-lg transition-colors",
        column.isVisible
          ? "bg-muted/30 hover:bg-muted/50"
          : "bg-muted/10 hover:bg-muted/20 opacity-60",
        isDragging && "shadow-lg bg-background border border-primary/30 opacity-90"
      )}
    >
      {/* Drag handle */}
      <div
        {...attributes}
        {...listeners}
        className={cn(
          "flex items-center justify-center w-6 h-6 rounded",
          isDraggable
            ? "cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground hover:bg-muted/50"
            : "text-muted-foreground/40 cursor-not-allowed"
        )}
      >
        {column.isFixed ? (
          <LockIcon className="h-3.5 w-3.5" />
        ) : (
          <DotsSixVerticalIcon className="h-4 w-4" />
        )}
      </div>

      {/* Column info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className={cn(
            "text-sm font-medium truncate",
            column.isVisible ? "text-foreground" : "text-muted-foreground"
          )}>
            {column.label}
          </span>
          {column.isCustomField && (
            <TagIcon className="h-3 w-3 text-primary/70 flex-shrink-0" />
          )}
        </div>
        {column.description && (
          <p className="text-xs text-muted-foreground truncate mt-0.5">
            {column.description}
          </p>
        )}
      </div>

      {/* Visibility toggle */}
      <div className="flex items-center gap-2">
        {isUpdating && (
          <CircleNotchIcon className="h-3.5 w-3.5 text-primary animate-spin" />
        )}
        <Switch
          checked={column.isVisible}
          onCheckedChange={onToggleVisibility}
          disabled={isDisabled || isUpdating || column.isFixed}
          className="data-[state=checked]:bg-primary"
        />
      </div>
    </div>
  );
}

export function TableCustomizerDialog({
  open,
  onOpenChange,
  columns,
  onColumnVisibilityChange,
  onColumnsReorder,
  fieldTypeOptions = [],
  onFieldTypeSelect,
  isLoading = false,
  canModify = true,
  title = 'Customize Table',
  description = 'Drag to reorder, toggle to show/hide columns',
}: TableCustomizerDialogProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [updatingColumnId, setUpdatingColumnId] = useState<string | null>(null);
  const [isReordering, setIsReordering] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Use shared drag-drop sensors
  const { sensors } = useDragDropSensors({ activationDistance: 5 });

  // Sort ALL columns (unfiltered) - visible first (by order), then hidden (alphabetically)
  // Fixed columns are pinned: first fixed column stays first, last fixed column stays last
  const allSortedColumns = (() => {
    const visible = columns
      .filter(col => col.isVisible)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

    // Enforce fixed column positions: fixed columns at the start stay at start,
    // fixed columns at the end stay at end
    const fixedStart: ColumnItem[] = [];
    const fixedEnd: ColumnItem[] = [];
    const movable: ColumnItem[] = [];

    // Determine original positions of fixed columns from the raw column definitions
    // Columns defined first in the array with isFixed => pinned to start
    // Columns defined last in the array with isFixed => pinned to end
    const originalFixed = columns.filter(c => c.isFixed && c.isVisible);
    const fixedEndIds = new Set<string>();
    const fixedStartIds = new Set<string>();

    if (originalFixed.length > 0) {
      // Walk from the start of original column order to find start-pinned
      for (const col of columns) {
        if (col.isFixed && col.isVisible) fixedStartIds.add(col.id);
        else break;
      }
      // Walk from the end to find end-pinned
      for (let i = columns.length - 1; i >= 0; i--) {
        const col = columns[i];
        if (col?.isFixed && col.isVisible) fixedEndIds.add(col.id);
        else break;
      }
    }

    for (const col of visible) {
      if (fixedStartIds.has(col.id)) fixedStart.push(col);
      else if (fixedEndIds.has(col.id)) fixedEnd.push(col);
      else movable.push(col);
    }

    const hidden = columns
      .filter(col => !col.isVisible)
      .sort((a, b) => a.label.localeCompare(b.label));
    return [...fixedStart, ...movable, ...fixedEnd, ...hidden];
  })();

  // All visible columns (unfiltered) for reordering
  const allVisibleColumns = allSortedColumns.filter(col => col.isVisible);

  // All hidden columns (unfiltered) for reordering
  const allHiddenColumns = allSortedColumns.filter(col => !col.isVisible);

  // Filter columns by search (for display only)
  const filteredColumns = (() => {
    if (!searchQuery.trim()) return allSortedColumns;
    const query = searchQuery.toLowerCase();
    return allSortedColumns.filter(col =>
      col.label.toLowerCase().includes(query) ||
      col.description?.toLowerCase().includes(query)
    );
  })();

  // Display-filtered visible and hidden columns
  const visibleColumns = filteredColumns.filter(col => col.isVisible);
  const hiddenColumns = filteredColumns.filter(col => !col.isVisible);
  const categorizedFieldTypeValues = new Set(
    FIELD_TYPE_CATEGORIES.flatMap(category => category.types)
  );
  const groupedFieldTypes = [
    ...FIELD_TYPE_CATEGORIES.map(category => ({
      ...category,
      fields: fieldTypeOptions.filter(fieldType =>
        category.types.includes(fieldType.value.toUpperCase())
      ),
    })),
    {
      id: 'uncategorized',
      label: 'More',
      types: [],
      fields: fieldTypeOptions.filter(
        fieldType => !categorizedFieldTypeValues.has(fieldType.value.toUpperCase())
      ),
    },
  ].filter(category => category.fields.length > 0);

  // Determine if we're filtering - affects drag behavior
  const isSearchActive = searchQuery.trim().length > 0;

  // For drag operations, we need to use a consistent list
  // When searching, disable reordering to avoid confusion
  // When not searching, use the full visible columns list
  const draggableColumns = isSearchActive ? visibleColumns : allVisibleColumns;

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id || !canModify) {
      return;
    }

    // Use draggableColumns which matches exactly what SortableContext and rendering use
    const oldIndex = draggableColumns.findIndex(col => col.id === active.id);
    const newIndex = draggableColumns.findIndex(col => col.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    // Prevent dragging fixed columns or dropping on fixed columns
    const activeColumn = draggableColumns[oldIndex];
    const targetColumn = draggableColumns[newIndex];
    if (activeColumn?.isFixed || targetColumn?.isFixed) {
      return;
    }

    // Reorder the draggable list
    let reorderedVisible = arrayMove(draggableColumns, oldIndex, newIndex);

    // Re-pin fixed columns: extract them, remove from list, re-insert at original positions
    const fixedEntries = draggableColumns
      .map((col, idx) => ({ col, idx }))
      .filter(e => e.col.isFixed);

    if (fixedEntries.length > 0) {
      const withoutFixed = reorderedVisible.filter(col => !col.isFixed);
      for (const { col, idx } of fixedEntries) {
        const insertAt = Math.min(idx, withoutFixed.length);
        withoutFixed.splice(insertAt, 0, col);
      }
      reorderedVisible = withoutFixed;
    }

    // Build the final column list
    let allReordered: ColumnItem[];
    if (isSearchActive) {
      // When searching, we need to merge reordered filtered items back into full list
      const newPositions = new Map(reorderedVisible.map((col, idx) => [col.id, idx]));

      const updatedVisible = allVisibleColumns.map(col => {
        const newPos = newPositions.get(col.id);
        if (newPos !== undefined) {
          return { ...col, order: newPos };
        }
        return col;
      }).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

      allReordered = [...updatedVisible, ...allHiddenColumns];
    } else {
      allReordered = [...reorderedVisible, ...allHiddenColumns];
    }

    setIsReordering(true);
    try {
      await onColumnsReorder(allReordered);
    } finally {
      setIsReordering(false);
    }
  };

  const handleToggleVisibility = async (columnId: string, isVisible: boolean) => {
    if (!canModify) return;

    setUpdatingColumnId(columnId);
    try {
      await onColumnVisibilityChange(columnId, isVisible);
    } finally {
      setUpdatingColumnId(null);
    }
  };

  const handleFieldTypeSelect = (fieldType: string) => {
    onFieldTypeSelect?.(fieldType);
    setSearchQuery('');
  };

  // Reset search when dialog opens
  useEffect(() => {
    if (open) {
      setSearchQuery('');
    }
  }, [open]);

  const hasAddFieldOption = fieldTypeOptions.length > 0 && onFieldTypeSelect;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          "max-h-[calc(100vh-2rem)] p-0 gap-0 overflow-hidden",
          hasAddFieldOption ? "sm:max-w-[920px]" : "sm:max-w-[480px]"
        )}
        style={{ pointerEvents: 'auto' }}
      >
        {/* Header */}
        <DialogHeader className="px-4 pt-4 pb-3 border-b border-border/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center">
              <SlidersHorizontalIcon className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1">
              <DialogTitle className="text-base font-semibold">{title}</DialogTitle>
              <DialogDescription className="text-xs text-muted-foreground mt-0.5">
                {description}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className={cn(
          "grid min-h-0 max-h-[calc(100vh-10rem)] overflow-y-auto sm:overflow-hidden",
          hasAddFieldOption ? "sm:grid-cols-[minmax(0,1fr)_minmax(280px,340px)]" : "grid-cols-1"
        )}>
          <section className={cn(
            "min-w-0 border-border/50",
            hasAddFieldOption && "sm:border-r"
          )}>
            <div className="border-b border-border/30 px-4 py-3">
              <div className="mb-2 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <EyeIcon className="h-3.5 w-3.5 text-primary" />
                  <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Columns
                  </span>
                </div>
                <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
                  {columns.length}
                </span>
              </div>
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search columns..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  autoFocus
                  onMouseDown={(e) => {
                    // Force focus — something in the Radix Dialog/DismissableLayer
                    // chain prevents the browser's default mousedown focus transfer
                    const input = e.currentTarget;
                    requestAnimationFrame(() => input.focus());
                  }}
                  className={cn(
                    "flex h-8 w-full rounded-md pl-8 pr-3 py-1 text-sm",
                    "bg-muted/30 border border-border/50 text-foreground",
                    "placeholder:text-muted-foreground/50",
                    "focus:border-primary/60 focus:outline-none",
                    "transition-colors duration-200 ease-out"
                  )}
                />
              </div>
            </div>

            {/* Column List */}
            <ScrollArea className="h-[320px] px-4 py-3 sm:h-[420px]">
              {isLoading ? (
                <div className="flex items-center justify-center py-8 text-muted-foreground">
                  <CircleNotchIcon className="h-5 w-5 animate-spin mr-2" />
                  <span className="text-sm">Loading columns...</span>
                </div>
              ) : filteredColumns.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <p className="text-sm">No columns match your search</p>
                </div>
              ) : (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <div className="space-y-4">
                    {/* Visible Columns Section */}
                    {draggableColumns.length > 0 && (
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <EyeIcon className="h-3.5 w-3.5 text-primary" />
                          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                            Visible ({draggableColumns.length})
                          </span>
                        </div>
                        <SortableContext
                          items={draggableColumns.map(col => col.id)}
                          strategy={verticalListSortingStrategy}
                        >
                          <div className="space-y-1">
                            {draggableColumns.map((column) => (
                              <SortableColumnRow
                                key={column.id}
                                column={column}
                                onToggleVisibility={(isVisible) => handleToggleVisibility(column.id, isVisible)}
                                isDisabled={!canModify || isReordering}
                                isUpdating={updatingColumnId === column.id}
                              />
                            ))}
                          </div>
                        </SortableContext>
                      </div>
                    )}

                    {/* Hidden Columns Section */}
                    {hiddenColumns.length > 0 && (
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <EyeSlashIcon className="h-3.5 w-3.5 text-muted-foreground" />
                          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                            Hidden ({hiddenColumns.length})
                          </span>
                        </div>
                        <div className="space-y-1">
                          {hiddenColumns.map((column) => (
                            <div
                              key={column.id}
                              className={cn(
                                "flex items-center gap-2 px-3 py-2 rounded-lg transition-all duration-150",
                                "bg-muted/10 hover:bg-muted/20 opacity-60 hover:opacity-80"
                              )}
                            >
                              <div className="flex items-center justify-center w-6 h-6 rounded text-muted-foreground/40">
                                <EyeSlashIcon className="h-3.5 w-3.5" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1.5">
                                  <span className="text-sm font-medium truncate text-muted-foreground">
                                    {column.label}
                                  </span>
                                  {column.isCustomField && (
                                    <TagIcon className="h-3 w-3 text-primary/70 flex-shrink-0" />
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                {updatingColumnId === column.id && (
                                  <CircleNotchIcon className="h-3.5 w-3.5 text-primary animate-spin" />
                                )}
                                <Switch
                                  checked={column.isVisible}
                                  onCheckedChange={(isVisible) => handleToggleVisibility(column.id, isVisible)}
                                  disabled={!canModify || updatingColumnId === column.id}
                                  className="data-[state=checked]:bg-primary"
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </DndContext>
              )}
            </ScrollArea>
          </section>

          {hasAddFieldOption && (
            <aside className="min-w-0 bg-muted/10">
              <div className="border-b border-border/30 px-4 py-3">
                <div className="flex items-center gap-2">
                  <PlusCircleIcon className="h-3.5 w-3.5 text-primary" />
                  <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Add Field
                  </span>
                </div>
                <p className="mt-1 text-xs leading-5 text-muted-foreground">
                  Pick a type to open setup. After saving, drag the new column on the left.
                </p>
              </div>
              <ScrollArea className="h-[320px] px-4 py-3 sm:h-[420px]">
                <div className="space-y-4">
                  {groupedFieldTypes.map((category) => (
                    <div key={category.id} className="space-y-1.5">
                      <div className="px-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        {category.label}
                      </div>
                      <div className="space-y-1">
                        {category.fields.map((fieldType) => (
                          <button
                            key={fieldType.value}
                            onClick={() => handleFieldTypeSelect(fieldType.value)}
                            className={cn(
                              "w-full flex items-start gap-2.5 px-3 py-2.5 rounded-lg text-left",
                              "border border-transparent bg-background/70 hover:bg-accent/60 hover:border-border/70",
                              "transition-all duration-150 group cursor-pointer"
                            )}
                          >
                            <div className={cn(
                              "flex-shrink-0 w-7 h-7 rounded-md flex items-center justify-center",
                              "bg-primary/10 border border-primary/10 group-hover:border-primary/30"
                            )}>
                              <PlusCircleIcon className="h-3.5 w-3.5 text-primary" weight="duotone" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">
                                {fieldType.label}
                              </div>
                              <div className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
                                {fieldType.description}
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </aside>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-border/50 bg-muted/10">
          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground">
              {allVisibleColumns.length} of {columns.length} columns visible
              {hasAddFieldOption ? ' · New fields appear here after save' : ''}
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onOpenChange(false)}
              className="h-8"
            >
              Done
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

TableCustomizerDialog.displayName = "TableCustomizerDialog";
